import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { ApiError } from '../../api/client';
import { getPermissions, ME_QUERY_KEY, PERMISSIONS_QUERY_KEY, savePermissions } from '../../api/endpoints/permissions';
import {
  BusinessPermissionsResponseSchema,
  type AccessProfile,
  type BusinessAccessRecord,
  type BusinessPermissionsResponse,
  type PermissionKey,
} from '../../api/schemas';
import { ErrorBanner } from '../../components/ErrorBanner';
import { LoadingState } from '../../components/LoadingState';
import type { PortalMode } from '../../lib/mode';
import { useVersionConflict } from '../../lib/use-version-conflict';
import { isVersionConflict } from '../../lib/version-conflict';
import {
  ALL_PERMISSIONS,
  COACH_AGENDA_HINT,
  PERMISSION_GROUPS,
  PROFILES,
  PROFILE_LABEL,
  profilesForMode,
  withPermission,
} from './catalog';
import { Switch } from './Switch';

type Props = {
  /** The accesses on file, to say who each profile reaches. */
  accesses: readonly BusinessAccessRecord[] | undefined;
  /** What kind of business this is: a waiter is only offered to a restaurant. */
  mode?: PortalMode;
};

/**
 * One press of a switch: the profile's whole new list, the version of that profile as the screen read it
 * (0 when it has no stored row) and a key for this attempt. `rollback` is where a refused resend goes back to.
 */
type Toggle = {
  profile: AccessProfile;
  next: PermissionKey[];
  version: number | undefined;
  key: string;
  rollback?: BusinessPermissionsResponse;
};

/** Every profile's list, which is what the version check compares: each list as one field. */
type PermissionsDraft = Record<AccessProfile, PermissionKey[]>;
type PermissionsRecord = BusinessPermissionsResponse['data'];

const PERMISSION_LABEL = new Map<PermissionKey, string>(
  PERMISSION_GROUPS.flatMap((group) => group.items.map((item) => [item.key, item.label] as const)),
);

function parsePermissions(current: unknown): PermissionsRecord | null {
  const parsed = BusinessPermissionsResponseSchema.shape.data.safeParse(current);
  return parsed.success ? parsed.data : null;
}

function listText(list: PermissionKey[]): string {
  return list.length === 0 ? 'Vacío' : list.map((key) => PERMISSION_LABEL.get(key) ?? key).join(', ');
}

/**
 * "Qué ve cada perfil": the owner picks a profile and switches each door on or
 * off. Every switch saves on its own; the API applies it on that person's next
 * request, so there is nothing to publish.
 */
export function PermisosSection({ accesses, mode }: Props) {
  const queryClient = useQueryClient();
  const [profile, setProfile] = useState<AccessProfile>('front_desk');
  const query = useQuery({ queryKey: PERMISSIONS_QUERY_KEY, queryFn: getPermissions });
  // The profile whose list is waiting for an answer to a version conflict, and the saved state a resend goes back to.
  const answering = useRef<{ profile: AccessProfile; saved: BusinessPermissionsResponse } | null>(null);

  const mutation = useMutation({
    mutationFn: ({ profile: target, next, version, key }: Toggle) =>
      version === undefined ? savePermissions(target, next, key) : savePermissions(target, next, key, { expectedVersion: version }),
    onMutate: async ({ profile: target, next, rollback }: Toggle) => {
      await queryClient.cancelQueries({ queryKey: PERMISSIONS_QUERY_KEY });
      const shown = queryClient.getQueryData<BusinessPermissionsResponse>(PERMISSIONS_QUERY_KEY);
      if (shown) queryClient.setQueryData(PERMISSIONS_QUERY_KEY, withProfile(shown, target, next));
      return { previous: rollback ?? shown, shown };
    },
    onError: (error, pressed, context) => {
      // A clash is not a refusal: the list is rebased on what is saved now instead of going back blindly.
      // What it compares against is what the press started from; for a resend, the saved lists it was rebased on.
      const started = pressed.rollback ?? context?.shown;
      if (isVersionConflict(error) && started) {
        const base = started.data.profiles;
        answering.current = { profile: pressed.profile, saved: started };
        if (conflict.handle(error, { base, mine: { ...base, [pressed.profile]: pressed.next } })) return;
      }
      if (context?.previous) queryClient.setQueryData(PERMISSIONS_QUERY_KEY, context.previous);
    },
    onSuccess: (saved, pressed) => {
      // Two quick presses can answer out of order: an older answer never replaces a newer one.
      const stored = queryClient.getQueryData<BusinessPermissionsResponse>(PERMISSIONS_QUERY_KEY)?.data.versions?.[pressed.profile];
      const answered = saved.data.versions?.[pressed.profile];
      if (stored !== undefined && answered !== undefined && stored > answered) return;
      queryClient.setQueryData(PERMISSIONS_QUERY_KEY, saved);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    },
  });

  const conflict = useVersionConflict<PermissionsRecord, PermissionsDraft>({
    keys: PROFILES,
    labels: PROFILE_LABEL,
    format: Object.fromEntries(PROFILES.map((candidate) => [candidate, listText])) as Record<AccessProfile, (list: PermissionKey[]) => string>,
    parse: parsePermissions,
    toDraft: (record) => record.profiles,
    rebase: ({ draft, record }) => {
      answering.current = answering.current && { profile: answering.current.profile, saved: { data: record } };
      queryClient.setQueryData(PERMISSIONS_QUERY_KEY, { data: { ...record, profiles: draft } });
    },
    save: ({ draft, version }) => {
      const target = answering.current;
      if (!target) return;
      mutation.mutate({ profile: target.profile, next: draft[target.profile], version, key: crypto.randomUUID(), rollback: target.saved });
    },
    current: () => queryClient.getQueryData<BusinessPermissionsResponse>(PERMISSIONS_QUERY_KEY)?.data.profiles ?? null,
    discard: (record) => queryClient.setQueryData(PERMISSIONS_QUERY_KEY, { data: record }),
  });

  /** One switch moved: the profile's whole list goes, naming the version it was read at. */
  function toggle(item: PermissionKey, allowed: boolean) {
    const shown = query.data?.data;
    if (!shown || conflict.asking) return;
    conflict.clear();
    mutation.mutate({
      profile,
      next: withPermission(shown.profiles[profile], item, allowed),
      version: shown.versions?.[profile],
      key: crypto.randomUUID(),
    });
  }

  const current = query.data?.data.profiles[profile] ?? [];
  const people = peopleWith(accesses, profile);

  return (
    <section className="section permisos" aria-labelledby="permisos-title">
      <h2 id="permisos-title" className="section-label">
        Qué ve cada perfil
      </h2>
      {query.isLoading ? <LoadingState label="Cargando permisos…" /> : null}
      {query.isError ? <ErrorBanner message={errorMessage(query.error)} /> : null}
      {query.data ? (
        <>
          <div className="seg permisos-profiles" role="group" aria-label="Perfil">
            {profilesForMode(mode, profile).map((candidate) => (
              <button
                key={candidate}
                type="button"
                aria-pressed={candidate === profile}
                onClick={() => setProfile(candidate)}
              >
                {PROFILE_LABEL[candidate]}
                <small>{countLabel(query.data.data.profiles[candidate])}</small>
              </button>
            ))}
          </div>
          <p className="permisos-people">
            {people.length > 0
              ? `${PROFILE_LABEL[profile]}: ${people.join(', ')}.`
              : `Nadie tiene el perfil ${PROFILE_LABEL[profile]} todavía. Se lo das a una persona en Accesos.`}
          </p>
          {PERMISSION_GROUPS.map((group) => (
            <div key={group.title} className="permisos-group">
              <h3 className="permisos-group-title">{group.title}</h3>
              <ul className="group rows">
                {group.items.map((item) => {
                  const titleId = `permiso-${item.key}`;
                  const hintId = `permiso-${item.key}-hint`;
                  const hint = profile === 'coach' && item.key === 'agenda' ? COACH_AGENDA_HINT : item.hint;
                  return (
                    <li key={item.key} className="row permisos-row">
                      <div className="grow">
                        <p className="row-title" id={titleId}>
                          {item.label}
                        </p>
                        <p className="row-sub" id={hintId}>
                          {hint}
                        </p>
                      </div>
                      <Switch
                        checked={current.includes(item.key)}
                        labelledBy={titleId}
                        describedBy={hintId}
                        disabled={conflict.asking}
                        onChange={(allowed) => toggle(item.key, allowed)}
                      />
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
          {mutation.isError && !isVersionConflict(mutation.error) ? <ErrorBanner message={errorMessage(mutation.error)} /> : null}
          {conflict.notice}
          <p className="permisos-foot" role="status" aria-live="polite">
            {mutation.isPending
              ? 'Guardando…'
              : 'Cada cambio aplica de una vez. Tú siempre ves todo; los accesos, estos permisos y el plan de Dale solo los manejas tú.'}
          </p>
        </>
      ) : null}
    </section>
  );
}

function withProfile(
  response: BusinessPermissionsResponse,
  profile: AccessProfile,
  next: PermissionKey[],
): BusinessPermissionsResponse {
  return { data: { ...response.data, profiles: { ...response.data.profiles, [profile]: next } } };
}

function peopleWith(accesses: readonly BusinessAccessRecord[] | undefined, profile: AccessProfile): string[] {
  return (accesses ?? [])
    .filter((access) => access.role === 'employee' && access.isActive && access.accessProfile === profile)
    .map((access) => access.name);
}

function countLabel(list: readonly PermissionKey[]): string {
  return `${list.length} de ${ALL_PERMISSIONS.length}`;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos guardar el permiso. Revisa tu conexión y vuelve a intentarlo.';
}
